import { observer } from 'mobx-react-lite';
import React from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useColumnDragState } from '../../common/hooks/use-column-drag-state';
import { QueueStore } from '../store/queue-store';
import { RawQueueRowData } from '../store/types-2';
import { QueueRow } from './queue-row';
import { QueueVirtuosoListWrapper } from './queue-virtuoso-list-wrapper';
import { LoadingQueueRows } from './loading-queue-rows';
import { Flex, InlineBanner } from '@cad-arc';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';

export const QueueRows = observer(function QueueRows({
    store,
    onRowDoubleClick,
    onClosePanel,
}: {
    store: QueueStore<RawQueueRowData>;
    onRowDoubleClick: (row: RawQueueRowData) => void;
    onClosePanel: () => void;
}): React.ReactElement {
    const { isDraggingColumn } = useColumnDragState();
    const data = store.listStore.data.list;
    const isLoading = store.isLoading;
    const computeItemKey = React.useCallback((index: number) => data[index].id, [data]);

    const renderItemContent = (index: number): React.ReactElement => {
        const item = data[index];
        const ret = (
            <QueueRow
                key={item.id}
                data={item}
                index={index}
                onDoubleClick={onRowDoubleClick}
                isSelected={store.isSelected}
                onRowSelect={store.setSelectedRowId}
                queueColumns={store.workspacePanel.queue.queueColumns}
            />
        );
        return ret;
    };

    if (store.isError) {
        return (
            <Flex h="100%" justifyContent="center" alignItems="center">
                <InlineBanner
                    status="error"
                    title="An Error Occurred"
                    description={errorOccurredWhile('loading queue data')}
                    actions={[{ children: 'Close Panel', onClick: () => onClosePanel() }]}
                />
            </Flex>
        );
    }

    return isLoading ? (
        <LoadingQueueRows />
    ) : (
        <Virtuoso
            key="list"
            style={{
                height: '100%',
                width: '100%',
                overflowY: isDraggingColumn ? 'hidden' : 'auto',
            }}
            totalCount={isLoading ? 50 : data.length}
            components={{ List: QueueVirtuosoListWrapper }}
            computeItemKey={computeItemKey}
            overscan={200}
            itemContent={renderItemContent}
        />
    );
});
